import { motion } from 'framer-motion';
import { ListChecks, UserPlus, Server, LogIn, Play, CreditCard } from 'lucide-react';

const steps = [
  { icon: ListChecks, title: 'Elegís el plan', desc: 'Seleccioná el que mejor se adapta a tu estudio.' },
  { icon: UserPlus, title: 'Creás tu cuenta', desc: 'Registrá tus datos y los de tu organización.' },
  { icon: Server, title: 'LexOrbit genera tu espacio', desc: 'Tu entorno privado se crea automáticamente.' },
  { icon: LogIn, title: 'Accedés al sistema', desc: 'Ingresá al panel de gestión de inmediato.' },
  { icon: Play, title: 'Probás 5 días gratis', desc: 'Explorá todas las funcionalidades sin compromiso.' },
  { icon: CreditCard, title: 'Activás tu plan', desc: 'Continuá con el plan elegido al finalizar la demo.' },
];

export default function HowItWorksSection() {
  return (
    <section className="relative py-20 lg:py-28 bg-[#050505]" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center mb-14">
          <span className="text-[#C9A44C] text-[11px] uppercase tracking-[0.2em] block mb-3">Proceso</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-[#F8F6F0] tracking-tight mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Cómo empieza tu estudio en <span className="text-[#C9A44C] italic">LexOrbit</span>
          </h2>
          <p className="text-[#B8B8B8] text-sm max-w-lg mx-auto leading-relaxed">
            En pocos pasos, tu estudio obtiene su propio entorno privado de gestión jurídica.
          </p>
        </motion.div>

        {/* Timeline desktop / cards mobile */}
        <div className="hidden lg:flex items-start justify-between gap-3 mb-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex-1 text-center relative" data-testid={`step-card-${i}`}>
                <div className="w-11 h-11 rounded-full bg-[#141414] border border-[rgba(201,164,76,0.25)] flex items-center justify-center mx-auto mb-3 relative z-10">
                  <Icon className="w-5 h-5 text-[#C9A44C]" strokeWidth={1.5} />
                </div>
                {i < steps.length - 1 && <div className="absolute top-[22px] left-[calc(50%+22px)] w-[calc(100%-44px)] h-px bg-[rgba(201,164,76,0.15)]" />}
                <span className="text-[#C9A44C]/40 text-[10px] font-bold block mb-1">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="text-[#F8F6F0] text-sm font-semibold mb-1">{s.title}</h3>
                <p className="text-[#B8B8B8] text-[11px] leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile grid */}
        <div className="grid sm:grid-cols-2 gap-4 lg:hidden">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-[#141414] border border-[rgba(201,164,76,0.12)] rounded-xl p-5 hover:border-[rgba(201,164,76,0.3)] transition-all duration-400"
                data-testid={`step-card-mobile-${i}`}>
                <div className="flex items-center gap-3 mb-2.5">
                  <span className="text-[#C9A44C]/40 text-xs font-bold">{String(i + 1).padStart(2, '0')}</span>
                  <Icon className="w-5 h-5 text-[#C9A44C]" strokeWidth={1.5} />
                </div>
                <h3 className="text-[#F8F6F0] text-sm font-semibold mb-1">{s.title}</h3>
                <p className="text-[#B8B8B8] text-[11px] leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
          className="text-center text-[#B8B8B8]/60 text-xs mt-8 max-w-xl mx-auto">
          La demo gratuita permite probar el sistema durante 5 días. Finalizado ese período, la organización deberá activar un plan pago para continuar utilizando LexOrbit.
        </motion.p>
      </div>
    </section>
  );
}
